import { PageModule } from '../../utils/page.module';
import { Param, readUrlParameter, writeToUrl } from '../../utils/url.utils';
import { getCatalogDetailPageFromUrl, ResourceDetailPageType } from '../../api/general-api';
import { createHtmlElementFromString } from '../../utils/html.utils';
import { header } from '../header/header.module';
import { getResourceDetailPageTypeFromResourceType } from '../../api/resources-api';
import { HamiltonResourceBaseType, ResourceType } from '../../api/search-api';
import { resourcesCenter } from './resources-center.module';
import { favoritesListData } from '../../favorites-list-data.module';

/**
 * Creates a custom detail page for the resource center.
 */
export class ResourcesCenterDetailModule extends PageModule {
  /**
   * Selector that is rendered in this view.
   */
  componentSelector = '.resources-center-detail';

  /**
   * Markup for the resource detail page
   */
  template = '<div class="resources-center-detail"></div>';

  /**
   * Markup for the loading indicators
   */
  loadingIndicatorMarkup = `<div class="indicators indicators--loading">
    <span class="indicator indicator--active"></span>
    <span class="indicator indicator--active"></span>
    <span class="indicator indicator--active"></span>
  </div>`;

  /**
   * URL to retrieve article detail pages.
   */
  articlesUrl: string;

  /**
   * URL to retrieve resources detail pages.
   */
  resourcesUrl: string;

  /**
   * Setup the component with translations
   * @param translations The translations to initialize
   * @param articlesUrl URL that can be used to retrieve the contents of the detail pages for articles.
   * @param resourcesUrl URL that can be used to retrieve the contents of the detail pages for resources.
   */
  constructor(translations: { [key: string]: string }, articlesUrl: string, resourcesUrl: string) {
    super();
    this.translations = translations;
    this.articlesUrl = articlesUrl;
    this.resourcesUrl = resourcesUrl;
  }

  /**
   * Add click listener for the back button on mobile.
   */
  addListeners(content: HTMLElement): void {
    const backButton = content.querySelector('.resources-detail-back-button');
    backButton?.removeAttribute('href');
    backButton?.addEventListener('click', () => {
      const selectedCategory = readUrlParameter(Param.CATEGORY);
      if (selectedCategory) {
        // return to the resources center page if there was a category selected before
        writeToUrl([], [Param.RESOURCE_DETAIL_ID, Param.RESOURCE_DETAIL_TYPE]);
        setTimeout(() => {
          if (window.localStorage && window.localStorage.getItem('scrollPosition')) {
            console.log('Scrolling to ', window.localStorage.getItem('scrollPosition'));
            window.scroll(0, parseInt(window.localStorage.getItem('scrollPosition')));
            window.localStorage.removeItem('scrollPosition');
          }
        }, 50);
      } else {
        // otherwise just go back to whichever page the user visited before.
        window.history.back();
      }
    });
    if (backButton) {
      const backSection = backButton.parentElement;
      backSection.classList.add('section__element--hidden-mobile');
    }
  }

  /**
   * Listen for clicks on the pin button.
   */
  setupFavoriteButton(
    htmlElement: HTMLElement,
    resourceId: string,
    resourceTitle: string,
    resourceImage: string,
    resourceType: HamiltonResourceBaseType,
    resourceTagValue: ResourceType
  ): void {
    const favoritesButton = htmlElement.querySelector('.resource-detail-favorite-button');

    const pinButtonClickListener = (): void => {
      // first of all check if the clicked resource is a favorite or not. This data is cached, so
      // if the users works in a different tab, then this data might be wrong. Pass forceUpdate: true,
      // if the data should be refreshed before adding/removing
      favoritesListData
        .getCachedFavoritesListResourceIds(false)
        .then((favoriteIds) => {
          const isFavorite = favoritesListData.isFavorite(resourceId, favoriteIds);
          const resourceFavoritesId = favoriteIds.find((favorite) => favorite.id === resourceId)?.favoritesItemId;

          if (isFavorite) {
            if (resourceFavoritesId) {
              // if the resource is a favorite -> remove it
              resourcesCenter.resourcesList.removeResourceFromFavorites(
                resourceTitle,
                resourceId,
                resourceImage,
                resourceFavoritesId,
                resourceType,
                resourceTagValue
              );
              favoritesButton.classList.toggle('button--primary', false);
            }
          } else {
            // otherwise add it to favorites
            resourcesCenter.resourcesList.addResourceToFavorites(
              resourceTitle,
              resourceId,
              resourceImage,
              resourceType,
              resourceTagValue
            );
            favoritesButton.classList.toggle('button--primary', true);
          }
        })
        .catch(() => {
          // user logged out -> remove the pin button
          favoritesButton?.parentElement.removeChild(favoritesButton);
        });
    };

    // listen for clicks
    favoritesButton?.addEventListener('click', pinButtonClickListener);
    this.updatePinButtonState();
  }

  /**
   * Update the already visible pin buttons.
   */
  updatePinButtonState(): void {
    if (this.pageComponent) {
      const pinButton = this.pageComponent.querySelector<HTMLElement>('.resource-detail-favorite-button');
      const resourceId =
        this.pageComponent.querySelector<HTMLElement>('[data-node-id]')?.getAttribute('data-node-id') || null;

      if (pinButton && resourceId) {
        favoritesListData
          .getCachedFavoritesListResourceIds(false)
          .then((resourcesInFavorites) => {
            const isFavorite = favoritesListData.isFavorite(resourceId, resourcesInFavorites);
            pinButton.classList.toggle('button--primary', isFavorite);
          })
          .catch(() => {
            // user logged out
            pinButton?.parentElement.parentElement.removeChild(pinButton.parentElement);
          });
      }
    }
  }

  /**
   * Create the detail page
   */
  renderView(): void {
    // hide the mobile menu
    header.hideCategoryHeader();

    // hide the ventilator dropdown and the search bar (only on mobile)
    header.hideVentilatorsAndSearchOnMobile();

    // hide the sub nav
    header.showSubNav(false);

    // Hide the headers shadow before user scrolled
    header.setHideShadowBeforeScrolled(true);

    // set the correct back button title
    resourcesCenter.updateMobileMenuHeadingAndBackButton(
      document.querySelector('.section--header'),
      null,
      null,
      'detail'
    );

    // read which resource needs to be displayed on this page
    const resourceId = readUrlParameter(Param.RESOURCE_DETAIL_ID);
    const resourceType: HamiltonResourceBaseType = readUrlParameter(
      Param.RESOURCE_DETAIL_TYPE
    ) as HamiltonResourceBaseType;
    const detailPageType = getResourceDetailPageTypeFromResourceType(resourceType);

    // Show loading indicator while content is loaded
    this.pageComponent.appendChild(createHtmlElementFromString(this.loadingIndicatorMarkup));
    getCatalogDetailPageFromUrl(
      detailPageType === ResourceDetailPageType.Resource ? this.resourcesUrl : this.articlesUrl,
      resourceId,
      resourceType,
      detailPageType
    ).then((html) => {
      // Remove loading indicator
      this.pageComponent.firstElementChild.remove();
      // Render content
      if (html.length > 0) {
        const div = document.createElement('div');
        div.innerHTML = html;
        this.addListeners(div);
        this.pageComponent.appendChild(div);

        // render the page title (provided by the API in an empty div)
        const pageTitle = div.querySelector<HTMLElement>('#detailTitle')?.innerText || '';
        document.title = pageTitle;
        const resourceImage =
          div.querySelector<HTMLElement>('[data-image-small]')?.getAttribute('data-image-small') || '';
        const resourceType = <HamiltonResourceBaseType>(
          div.querySelector<HTMLElement>('[data-resource-type]')?.getAttribute('data-resource-type')
        );
        const resourceTagValue = <ResourceType>(
          div.querySelector<HTMLElement>('[data-resource-tag-value]')?.getAttribute('data-resource-tag-value')
        );

        this.setupFavoriteButton(div, resourceId, pageTitle, resourceImage, resourceType, resourceTagValue);

      }
    });
  }
}
